import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BlogCTAButtons from "@/components/BlogCTAButtons";
import CanonicalTag from "@/components/CanonicalTag";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import heroImage from "@/assets/full-service-adu-solutions.png";

const BlogFullServiceADU = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Full Service ADU Solutions in California: Design, Permits, and Construction Under One Team",
    "description": "Learn why a full service ADU approach with one team handling design, permitting, and construction leads to smoother projects and better results.",
    "datePublished": "2026-04-10",
    "author": { "@type": "Organization", "name": "UDA ADU" },
    "publisher": { "@type": "Organization", "name": "UDA ADU" },
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/blogs/full-service-adu-solutions" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navigation />
      <div className="w-full max-w-5xl mx-auto px-4 pt-8">
        <img src={heroImage} alt="Full service ADU solutions - design, permits, and construction" className="w-full rounded-2xl" />
      </div>
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/blogs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Blog
        </Link>
        <p className="text-sm text-muted-foreground mb-2">April 10, 2026</p>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">Full Service ADU Solutions in California: Design, Permits, and Construction Under One Team</h1>
        <article className="prose prose-lg max-w-none text-muted-foreground space-y-6">
          <p>Most homeowners don't realize this at the beginning.</p>
          <p>Building an ADU is not just about construction. It's a process that involves design decisions, city approvals, engineering, and coordination across multiple moving parts. When those pieces are handled by different companies, things start to break down.</p>
          <p>That's where most projects run into delays, confusion, and unexpected costs.</p>
          <p>A full service ADU approach fixes that.</p>
          <p>At UDA ADU, everything is handled under one team. Design, permitting, and construction all move together. That means you are not trying to manage different consultants or chase updates from multiple people. You have one point of contact and one clear process from start to finish.</p>
          <p>The first step is always understanding the property. Before design begins, we look at zoning, setbacks, lot coverage, and utility access. This defines what is actually possible. Not what looks good online, but what can be approved and built.</p>
          <p>Once that is clear, the design phase moves forward with purpose. Layout, size, and function are planned around how the space will be used. Whether the goal is rental income, family housing, or long term flexibility, every decision ties back to that use.</p>
          <p>Permitting is where many projects slow down. Cities like Los Altos, Palo Alto, and San Jose have strict requirements. Plans need to be complete, accurate, and aligned with local codes. Because our team handles this in-house, we know what each city is looking for and how to move through the process without unnecessary back and forth.</p>
          <p>When construction begins, the difference becomes even more clear.</p>
          <p>There is no gap between the design team and the build team. Everyone is working from the same plan, the same expectations, and the same timeline. This reduces mistakes and keeps the project moving.</p>
          <p>Cost control is another major benefit of a full service approach. Because everything is planned early, there are fewer surprises later. Any upgrades or changes are clearly discussed before they happen.</p>
          <p>Homeowners always ask what makes a project go smoothly.</p>
          <p>It is not luck. It is structure.</p>
          <p>Clear planning. One team. Consistent communication.</p>
          <p>That is what keeps a project on track.</p>
          <p>In today's California market, ADUs are more than extra space. They are a long term asset. They can generate rental income, increase property value, and create flexibility for families.</p>
          <p>But that only happens when the project is done right from the beginning.</p>
          <p>A full service ADU solution is not about doing more. It is about doing it in the right order, with the right team.</p>
        </article>
        <BlogCTAButtons />
      </main>
      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default BlogFullServiceADU;
